import { LIGHTNING_STRIKE_COLOR, LIGHTNING_STRIKE_INNER_COLOR } from './colors';
import type { LightningGroup } from './types';

/**
 * SPAWN 4, authored in the lightning lab and pasted back.
 *
 * Same rules as ./spawn-1: flat, explicit, replaceable wholesale, and THE
 * LINE IDS ARE LOAD-BEARING -- index.tsx seeds each bolt's jag from the id's
 * characters, so renaming the UUID below reshapes that bolt. The numbering
 * skips 4-3, which was cut; the rest kept their names rather than being
 * renumbered into new shapes.
 *
 * Runs 1.08s end to end. Rotated 192deg, so it strikes back across the
 * engine against the other groups.
 *
 * AUTHORING ARTEFACTS ARE PRESERVED HERE RATHER THAN CLEANED UP, because
 * this file is a record of what was tuned. Each is flagged inline, and
 * sampleLightningTrack in ../motion.ts is what decides how they resolve:
 * stacked times let the LAST entry win, and a keyframe past a strand's own
 * lifetime is simply never reached, so the previous value holds to the end.
 */
export const LIGHTNING_SPAWN_4_GROUP: LightningGroup = {
  id: 'spawn-4',
  name: 'Spawn 4',
  enabled: true,
  position: [-0.28, -0.03, -0.1],
  rotationDeg: [0, 0, 192],
  scale: 2,
  lines: [
    // NOTE: four keyframes stacked at time 0 (0.5, 0, 0.7, 0). The LAST wins,
    // so this opens DARK at 0 with endOffset [0, -0.19, 0] and the first
    // three are dead data. Its only other keyframe is at 0.93, past the
    // 0.482 lifetime, so the strand spends its whole life ramping from 0
    // toward 1 and is cut off around half way -- a slow swell, never a hit.
    {
      id: 'strand-4-1',
      enabled: true,
      start: [-0.01, 0.03, 0],
      end: [0.84, 0.39, 0],
      delay: 0.25,
      lifetime: 0.482,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.0016,
      jag: 0.018,
      detail: 16,
      flickerHz: 7,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 0.5, endOffset: [0, 0, 0] },
        { time: 0.1, intensity: 0, endOffset: [0, -0.2, 0] },
        { time: 0.3, intensity: 0.7, endOffset: [0, -0.21, 0] },
        { time: 0.6, intensity: 0, endOffset: [0, -0.19, 0] },
        { time: 0.93, intensity: 1, endOffset: [0, -0.12, 0] },
      ],
    },
    // Closing keyframe at 0.93 is past the 0.482 lifetime, so the climb back
    // to 1 never happens: this strand blinks out at 0.186 and stays dark.
    {
      id: 'strand-4-2',
      enabled: true,
      start: [0, 0.018, 0],
      end: [0.78, 0.275, 0],
      delay: 0.27,
      lifetime: 0.482,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.0016,
      jag: 0.02,
      detail: 20,
      flickerHz: 8.5,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 0.5, endOffset: [0, 0, 0] },
        { time: 0.15, intensity: 1, endOffset: [0, 0.07, 0] },
        { time: 0.185, intensity: 0, endOffset: [0, 0.09, 0] },
        { time: 0.93, intensity: 1, endOffset: [0, 0.2, 0] },
      ],
    },
    // The double-tap, and the longest strand at 0.8. Its track ENDS at 0.168
    // on intensity 0, so it fires twice inside the first fifth of its life
    // and then holds dark for the remaining 0.63 -- it is what sets the
    // group's length without being visible for most of it.
    {
      id: 'strand-4-4',
      enabled: true,
      start: [0, 0.024, 0],
      end: [0.9, 0.345, 0],
      delay: 0.28,
      lifetime: 0.8,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.0016,
      jag: 0.03,
      detail: 16,
      flickerHz: 11.5,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 1, endOffset: [0, 0, 0] },
        { time: 0.12, intensity: 0, endOffset: [0, 0.07, 0] },
        { time: 0.14, intensity: 1, endOffset: [0, 0.09, 0] },
        { time: 0.16, intensity: 0, endOffset: [0, 0.11, 0] },
      ],
    },
    // Earliest cue of the group (0.15) and the sharpest: full, out, full,
    // out inside 0.168. Its last keyframe at 1 is past the 0.482 lifetime
    // and never lands, so it stays dark once it has snapped off.
    {
      id: '4491f2b5-3e53-4ef7-ad59-9701c8abc002',
      enabled: true,
      start: [-0.11, 0.114, -0.01],
      end: [0.9, 0.115, -0.11],
      delay: 0.15,
      lifetime: 0.482,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.0016,
      jag: 0.03,
      detail: 16,
      flickerHz: 11.5,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 1, endOffset: [0, 0, 0] },
        { time: 0.12, intensity: 0, endOffset: [0, 0.07, 0] },
        { time: 0.14, intensity: 1, endOffset: [0, 0.09, 0] },
        { time: 0.16, intensity: 0, endOffset: [0, 0.11, 0] },
        { time: 1, intensity: 1, endOffset: [0, 0.12, 0] },
      ],
    },
  ],
};
